import React, { useCallback, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchCompanyDetails } from "../../../../api/action/company.jsx";
import { getAllProductCategories, getAllProducts } from "../../../../api/action/products.jsx";
import { Loader } from "../../../../components/Loader.jsx";
import FilterPanel from "./FilterPanel.jsx";
import ProductsList from "./ProductTiles.jsx";
import { academyLogger } from "utils/logger";

const TFCategoryProducts = () => {
  const dispatch = useDispatch();
  const fullUrl = window.location.href;
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState([0, 0]);
  const [maxPrice, setMaxPrice] = useState(0);
  const [sortingOrder, setSortingOrder] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});

  const domain = fullUrl.split("/").slice(0, 3).join("/");
  const { name } = useParams();
  const companyPrimaryId = useSelector((state) => state.companyReducer.company?.primaryId);
  const [filteredCategoryProducts, setFilteredCategoryProducts] = useState([]);

  const fetchProducts = useCallback(async (company, manualCategory = null) => {
    try {
      let products = await getAllProducts(company)(dispatch);
      products = Array.isArray(products) ? products : [];
      const categoryToUse = manualCategory || name;
      if (categoryToUse && categoryToUse !== "All") {
        products = products.filter(
          (product) => product.category?.name === categoryToUse
        );
      }
      setFilteredCategoryProducts(products);

      const optionalAttributes = [];
      products.forEach((product) => {
        product.optionalAttributes?.forEach((attr) => {
          optionalAttributes.push({
            name: attr.name,
            values: attr.values.map((v) => v.value),
          });
        });
      });

      setCategoryProducts(products);
      setIsLoading(false);

      const res = await dispatch(getAllProductCategories(company));
      setCategories([{ code: "All", name: "All" }, ...(Array.isArray(res) ? res : [])]);

      if (products.length > 0) {
        const highestPrice = Math.max(
          ...products.map((product) => parseFloat(product.price.priceAmt))
        );
        const roundedMaxPrice = Math.ceil(highestPrice);
        setMaxPrice(roundedMaxPrice);
        setSelectedPriceRange([0, roundedMaxPrice]);
      } else {
        setMaxPrice(0);
        setSelectedPriceRange([0, 0]);
      }
    } catch (error) {
      academyLogger.error("Error fetching products:", error);
      setIsLoading(false);
    }
  }, [dispatch, name]);

  useLayoutEffect(() => {
    if (!companyPrimaryId) {
      setIsLoading(false);
      return;
    }
    fetchCompanyDetails(domain).then(() => {
      fetchProducts(companyPrimaryId);
    });
  }, [companyPrimaryId, domain, fetchProducts]);

  return (
    <div className="flex flex-col items-center p-4 bg-teal-50 min-h-screen">
      {isLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <Loader />
        </div>
      ) : (
        <>
          <h1 className="text-3xl font-semibold text-teal-700 mb-4">
            Products
          </h1>
          <div className="flex w-full">
            {/* Sidebar */}
            <div className="w-1/4 bg-white shadow-lg rounded-lg p-4 border border-teal-300">
              <FilterPanel
                categories={categories}
                selectedCategories={selectedCategories}
                handleCategoryChange={(filterVal) =>
                  setSelectedCategories((prev) =>
                    prev.includes(filterVal.code)
                      ? prev.filter((code) => code !== filterVal.code)
                      : [...prev, filterVal.code]
                  )
                }
                sortingOrder={sortingOrder}
                toggleSortingOrder={setSortingOrder}
                handleClearFilters={() => {
                  setSelectedCategories([]);
                  setSelectedPriceRange([0, maxPrice]);
                  setSortingOrder(null);
                  setSelectedAttributes({});
                }}
                selectedPriceRange={selectedPriceRange}
                setSelectedPriceRange={setSelectedPriceRange}
                maxPrice={maxPrice}
                selectedAttributes={selectedAttributes}
                handleAttributeChange={(attrName, attrValue) => {
                  setSelectedAttributes((prev) => {
                    const updatedAttributes = { ...prev };
                    if (updatedAttributes[attrName]) {
                      if (updatedAttributes[attrName].includes(attrValue)) {
                        updatedAttributes[attrName] = updatedAttributes[attrName].filter(
                          (val) => val !== attrValue
                        );
                      } else {
                        updatedAttributes[attrName].push(attrValue);
                      }
                    } else {
                      updatedAttributes[attrName] = [attrValue];
                    }
                    return updatedAttributes;
                  });
                }}
                filteredProducts={filteredCategoryProducts}
              />
            </div>

            {/* Products List */}
            <div className="flex flex-col w-full overflow-y-auto p-4">
              <ProductsList products={categoryProducts} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TFCategoryProducts;
